import { Alert, Button, Space } from 'antd'
import type { DuplicateCheck, LeadDraft } from '../../services/chatWorkspaceApi'

/** Верх окна «Добавить лида»: что нашла проверка на дубль и что будет сделано. */
export function LeadDraftDuplicate({ draft }: { draft: LeadDraft | null }) {
    if (!draft) return null
    const crm = draft.duplicate?.crm
    const lead = draft.duplicate?.lead
    if (draft.scenario === 'unavailable') {
        return (
            <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 14 }}
                message="CRM Wiseway не ответила — проверка на дубль не выполнена"
                description="Карточку создать нельзя, пока не проверим дубль. Нажмите «Проверить ещё раз»."
            />
        )
    }
    if (draft.scenario === 'create') return null

    const extra: { text: string; danger?: boolean }[] = []
    if (crm && crm.count > 1) extra.push({ text: `Найдено карточек: ${crm.count}, берём самую старую` })
    if (crm?.fraud_other_id) extra.push({ text: `Среди совпадений есть карточка во фрод-статусе: #${crm.fraud_other_id}`, danger: true })

    if (draft.scenario === 'merge_fraud' && crm) {
        return (
            <Alert
                type="error"
                showIcon
                style={{ marginBottom: 14 }}
                message={`Дубль во фрод-статусе: ${crm.status_label || crm.status}`}
                description={
                    <Space direction="vertical" size={2}>
                        <span>Карточка в CRM WW #{crm.id} будет подтянута для фиксации обращения, в работу не берётся.</span>
                        <span>Диалог уйдёт в «Закрытые».</span>
                        {extra.map((line) => <span key={line.text}>{line.text}</span>)}
                    </Space>
                }
            />
        )
    }
    const head = crm
        ? `Дубль: карточка в CRM WW #${crm.id}, ответственный ${crm.owner_name || 'не назначен'}, статус ${crm.status_label || crm.status || 'не указан'}`
        : lead
            ? `Дубль: карточка в Оркестраторе #${lead.id}, ответственный ${lead.owner_name || 'не назначен'}`
            : 'Дубль'
    return (
        <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 14 }}
            message={head}
            description={
                <Space direction="vertical" size={2}>
                    <span>Новая карточка не создаётся: диалог объединится с этой карточкой, ответственному — задача «Повторная заявка».</span>
                    {extra.map((line) => (
                        <span key={line.text} style={line.danger ? { color: FRAUD_COLOR, fontWeight: 600 } : undefined}>{line.text}</span>
                    ))}
                </Space>
            }
        />
    )
}

const FRAUD_COLOR = '#cf1322'
const DUPLICATE_COLOR = '#d46b08'

/** Метка «Дубль» в строке списка «Чатов». Фрод-статус - красная. */
export function DuplicateChip({ check }: { check?: DuplicateCheck | null }) {
    if (!check?.is_duplicate) return null
    const color = check.crm?.fraud ? FRAUD_COLOR : DUPLICATE_COLOR
    return (
        <span className="chat-chip" style={{ color, borderColor: color }}>
            {check.crm?.fraud ? 'Дубль · фрод' : 'Дубль'}
        </span>
    )
}

/** Строка в шапке диалога без карточки: что нашли в CRM Wiseway и в Оркестраторе. */
export function DuplicateNotice({
    check,
    onRecheck,
    rechecking,
}: {
    check?: DuplicateCheck | null
    onRecheck?: () => void
    rechecking?: boolean
}) {
    if (!check) return null
    if (check.state === 'failed') {
        return (
            <Alert
                type="warning"
                showIcon
                message={`Проверка на дубль не выполнена: ${check.error || 'CRM Wiseway не ответила'}. Повторится при «Добавить лида».`}
                action={onRecheck && (
                    <Button size="small" loading={rechecking} onClick={onRecheck}>Проверить ещё раз</Button>
                )}
            />
        )
    }
    if (!check.is_duplicate) return null

    const lines: { text: string; danger?: boolean }[] = []
    const crm = check.crm
    if (crm) {
        lines.push({
            text: `Уже есть в CRM WW: карточка #${crm.id}, ответственный ${crm.owner_name || 'не назначен'}, статус ${crm.status_label || crm.status || 'не указан'}`,
            danger: crm.fraud,
        })
        if (crm.count > 1) lines.push({ text: `Найдено карточек: ${crm.count}, берём самую старую` })
        if (crm.fraud_other_id) lines.push({ text: `Среди совпадений есть карточка во фрод-статусе: #${crm.fraud_other_id}`, danger: true })
    }
    if (check.lead) {
        lines.push({
            text: `Уже есть в Оркестраторе: карточка #${check.lead.id}, ответственный ${check.lead.owner_name || 'не назначен'}`,
        })
    }
    const danger = lines.some((line) => line.danger)
    return (
        <Alert
            type={danger ? 'error' : 'warning'}
            showIcon
            message={
                <Space direction="vertical" size={2}>
                    {lines.map((line) => (
                        <span key={line.text} style={line.danger ? { color: FRAUD_COLOR, fontWeight: 600 } : undefined}>{line.text}</span>
                    ))}
                </Space>
            }
        />
    )
}
